import React, { useEffect, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { supabase } from "@/integrations/supabase/client";
import { DashboardShell } from "@/components/dashboard/DashboardShell";
import { Loader2, CheckCircle2, AlertCircle, BookOpen, Sparkles, LayoutList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { motion, AnimatePresence } from "framer-motion";

type RequestStatus = "pending" | "processing" | "completed" | "failed";

interface GenerationRequest {
  id: string;
  status: RequestStatus;
  course_id: string | null;
  error_message: string | null;
  source: string;
  prompt: string | null;
}

const PROGRESS_STEPS = [
  { key: "analyzing", label: "Analyzing your request", icon: Sparkles },
  { key: "generating", label: "Generating curriculum", icon: LayoutList },
  { key: "building", label: "Building lessons & quizzes", icon: BookOpen },
];

const ROTATING_MESSAGES: Record<number, string[]> = {
  0: [
    "Reading your prompt…",
    "Understanding your learning goals…",
    "Identifying key topics…",
  ],
  1: [
    "Structuring the learning path…",
    "Organizing lessons in the right order…",
    "Designing prerequisite connections…",
  ],
  2: [
    "Writing concept explanations…",
    "Crafting practice questions…",
    "Calibrating difficulty levels…",
  ],
};

const PROGRESS_TARGETS: Record<number, number> = {
  0: 22,
  1: 55,
  2: 85,
  3: 100,
};

/* ─── Rotating Microcopy ─── */
const RotatingMessage: React.FC<{ step: number }> = ({ step }) => {
  const [msgIdx, setMsgIdx] = useState(0);
  const messages = ROTATING_MESSAGES[step] ?? [];

  useEffect(() => {
    setMsgIdx(0);
    if (messages.length <= 1) return;
    const iv = setInterval(() => setMsgIdx((p) => (p + 1) % messages.length), 3500);
    return () => clearInterval(iv);
  }, [step, messages.length]);

  if (!messages.length) return null;

  return (
    <AnimatePresence mode="wait">
      <motion.span
        key={`${step}-${msgIdx}`}
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.25 }}
        className="block text-xs text-muted-foreground"
      >
        {messages[msgIdx]}
      </motion.span>
    </AnimatePresence>
  );
};

/* ─── Course Preview Skeleton ─── */
const CoursePreviewSkeleton: React.FC<{ activeStep: number }> = ({ activeStep }) => {
  const isCompleted = activeStep >= 3;

  return (
    <motion.div
      className="hidden sm:flex flex-col gap-4 lg:gap-5 w-full"
      animate={isCompleted ? { scale: 1.02, opacity: 0.6 } : { scale: 1, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeInOut" }}
    >
      {/* Hero banner skeleton */}
      <motion.div
        className={`rounded-2xl h-32 lg:h-40 ${isCompleted ? "" : "shimmer-block"}`}
        style={{ filter: activeStep === 0 ? "blur(2px)" : "blur(0px)" }}
        animate={{ filter: activeStep === 0 ? "blur(2px)" : "blur(0px)" }}
        transition={{ duration: 0.6 }}
      />

      {/* Title + description */}
      <div className="space-y-2 px-1">
        <div className={`h-5 w-3/4 rounded-md ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
        <div className={`h-3.5 w-1/2 rounded-md ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
      </div>

      {/* Lesson card skeletons */}
      <AnimatePresence>
        {activeStep >= 1 && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            className="space-y-3"
          >
            {[0, 1, 2, 3].map((i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 * i, duration: 0.3 }}
              >
                <Card className="border-border/60 p-4 space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0 space-y-2">
                      {/* Type badge placeholder */}
                      <div className={`h-3 w-16 rounded ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
                      {/* Title */}
                      <div className={`h-4 rounded ${isCompleted ? "bg-muted" : "shimmer-block"}`} style={{ width: `${70 - i * 8}%` }} />
                      {/* Subtitle */}
                      <div className={`h-3 w-2/5 rounded ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
                    </div>
                    {/* CTA placeholder */}
                    <div className={`h-8 w-20 rounded-lg flex-shrink-0 ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
                  </div>

                  {/* Content step dots (step 2+) */}
                  <AnimatePresence>
                    {activeStep >= 2 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        transition={{ delay: 0.1 * i, duration: 0.3 }}
                        className="flex gap-1.5 pt-1"
                      >
                        {[0, 1, 2].map((d) => (
                          <div key={d} className={`h-1.5 w-1.5 rounded-full ${isCompleted ? "bg-muted-foreground/30" : "shimmer-block"}`} />
                        ))}
                        <div className={`h-1.5 flex-1 rounded ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Side cards: progress + XP */}
      <AnimatePresence>
        {activeStep >= 1 && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="grid grid-cols-2 gap-3"
          >
            <Card className="p-4 space-y-2 border-border/60">
              <div className={`h-3 w-20 rounded ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
              <div className={`h-6 w-14 rounded ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
              <div className={`h-2 w-full rounded-full ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
            </Card>
            <AnimatePresence>
              {activeStep >= 2 && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.2, duration: 0.3 }}
                >
                  <Card className="p-4 space-y-2 border-border/60 h-full">
                    <div className={`h-3 w-12 rounded ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
                    <div className={`h-6 w-16 rounded ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
                    <div className={`h-2 w-3/4 rounded-full ${isCompleted ? "bg-muted" : "shimmer-block"}`} />
                  </Card>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/* ─── Main Page ─── */
const CourseCreatingPage: React.FC = () => {
  usePageMeta("Creating Course — Shattara AI", "", { noIndex: true });
  const { requestId } = useParams<{ requestId: string }>();
  const navigate = useNavigate();
  const [request, setRequest] = useState<GenerationRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [progressValue, setProgressValue] = useState(0);
  const stepTimerRef = useRef<NodeJS.Timeout | null>(null);
  const progressTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Fetch initial request data
  useEffect(() => {
    if (!requestId) {
      navigate("/dashboard");
      return;
    }

    const fetchRequest = async () => {
      const { data, error } = await supabase
        .from("course_generation_requests")
        .select("id, status, course_id, error_message, source, prompt")
        .eq("id", requestId)
        .single();

      if (error || !data) {
        console.error("Request not found:", error);
        navigate("/dashboard");
        return;
      }

      setRequest(data as GenerationRequest);
      setLoading(false);

      if (data.status === "completed" && data.course_id) {
        navigate(`/course/${data.course_id}`, { replace: true });
      }
    };

    fetchRequest();
  }, [requestId, navigate]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!requestId) return;

    const channel = supabase
      .channel(`course-gen-${requestId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "course_generation_requests",
          filter: `id=eq.${requestId}`,
        },
        (payload) => {
          const updated = payload.new as GenerationRequest;
          setRequest(updated);

          if (updated.status === "completed" && updated.course_id) {
            setTimeout(() => {
              navigate(`/course/${updated.course_id}`, { replace: true });
            }, 1500);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [requestId, navigate]);

  // Animate progress steps
  useEffect(() => {
    if (!request) return;

    if (request.status === "pending") {
      setActiveStep(0);
    } else if (request.status === "processing") {
      setActiveStep(1);
      stepTimerRef.current = setTimeout(() => {
        setActiveStep(2);
      }, 8000);
    } else if (request.status === "completed") {
      setActiveStep(3);
    }

    return () => {
      if (stepTimerRef.current) clearTimeout(stepTimerRef.current);
    };
  }, [request?.status]);

  // Smooth continuous progress bar
  useEffect(() => {
    const target = PROGRESS_TARGETS[activeStep] ?? 0;

    // For non-completed steps, slowly creep toward target
    if (activeStep < 3) {
      // Immediate small jump
      setProgressValue((prev) => Math.max(prev, target - 15));
      // Then ease toward target
      progressTimerRef.current = setTimeout(() => {
        setProgressValue(target);
      }, 300);
    } else {
      // Completed: snap to 100
      setProgressValue(100);
    }

    return () => {
      if (progressTimerRef.current) clearTimeout(progressTimerRef.current);
    };
  }, [activeStep]);

  const handleRetry = useCallback(async () => {
    if (!request) return;

    await supabase
      .from("course_generation_requests")
      .update({ status: "pending", error_message: null })
      .eq("id", request.id);

    setRequest({ ...request, status: "pending", error_message: null });
    setActiveStep(0);
    setProgressValue(0);

    supabase.functions
      .invoke("generate-course", { body: { requestId: request.id } })
      .catch((err) => console.error("Retry invoke failed:", err));
  }, [request]);

  if (loading) {
    return (
      <DashboardShell>
        <div className="flex flex-1 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </DashboardShell>
    );
  }

  if (!request) return null;

  const isFailed = request.status === "failed";
  const isCompleted = request.status === "completed";

  return (
    <DashboardShell>
      <div className="flex flex-1 items-center justify-center px-4 py-8">
        <div className="w-full max-w-5xl flex flex-col lg:flex-row gap-8 lg:gap-12 items-start">
          {/* ─── Left Column: Progress ─── */}
          <div className="w-full lg:w-[40%] space-y-6 ambient-glow">
            {/* Header */}
            <div className="space-y-3">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="relative flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10"
              >
                {isFailed ? (
                  <AlertCircle className="h-7 w-7 text-destructive" />
                ) : isCompleted ? (
                  <>
                    <CheckCircle2 className="h-7 w-7 text-primary" />
                    {/* Celebration pulse */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl"
                      style={{
                        background: "radial-gradient(circle, hsl(var(--primary) / 0.3) 0%, transparent 70%)",
                      }}
                      initial={{ scale: 0, opacity: 0.6 }}
                      animate={{ scale: 1.8, opacity: 0 }}
                      transition={{ duration: 0.8, ease: "easeOut" }}
                    />
                  </>
                ) : (
                  <Sparkles className="h-7 w-7 text-primary animate-pulse" />
                )}
              </motion.div>

              <h1 className="text-xl font-semibold text-foreground">
                {isFailed
                  ? "Course generation failed"
                  : isCompleted
                  ? "Your course is ready!"
                  : "Creating your course"}
              </h1>

              {request.prompt && !isCompleted && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  "{request.prompt}"
                </p>
              )}
            </div>

            {/* Continuous progress bar */}
            {!isFailed && (
              <div className="space-y-1">
                <Progress
                  value={progressValue}
                  className={`h-1.5 transition-all duration-300 ${
                    isCompleted
                      ? "[&>div]:bg-[hsl(var(--success))] bg-[hsl(var(--success)/0.2)]"
                      : ""
                  }`}
                />
                <p className="text-xs text-muted-foreground tabular-nums text-right">
                  {Math.round(progressValue)}%
                </p>
              </div>
            )}

            {/* Progress Steps */}
            {!isFailed && (
              <div className="space-y-3">
                {PROGRESS_STEPS.map((step, idx) => {
                  const StepIcon = step.icon;
                  const isDone = activeStep > idx;
                  const isActive = activeStep === idx;

                  return (
                    <motion.div
                      key={step.key}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.15, duration: 0.3 }}
                      className={`flex items-start gap-3 rounded-xl border p-3 transition-colors ${
                        isDone
                          ? "border-primary/20 bg-primary/5"
                          : isActive
                          ? "border-primary/30 bg-primary/5"
                          : "border-border bg-card"
                      }`}
                    >
                      <motion.div
                        className={`flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0 transition-colors ${
                          isDone
                            ? "bg-primary text-primary-foreground"
                            : isActive
                            ? "bg-primary/20 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                        animate={
                          isDone
                            ? { scale: [0.8, 1.1, 1] }
                            : {}
                        }
                        transition={{ duration: 0.3 }}
                      >
                        {isDone ? (
                          <CheckCircle2 className="h-4 w-4" />
                        ) : isActive ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <StepIcon className="h-4 w-4" />
                        )}
                      </motion.div>
                      <div className="min-w-0">
                        <span
                          className={`text-sm font-medium ${
                            isDone || isActive ? "text-foreground" : "text-muted-foreground"
                          }`}
                        >
                          {step.label}
                        </span>
                        {isActive && !isCompleted && (
                          <RotatingMessage step={idx} />
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Error state */}
            {isFailed && (
              <div className="space-y-4">
                <div className="rounded-xl border border-destructive/20 bg-destructive/5 p-4">
                  <p className="text-sm text-destructive">
                    {request.error_message || "An unexpected error occurred while generating the course."}
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button variant="outline" onClick={() => navigate("/dashboard")}>
                    Back to Dashboard
                  </Button>
                  <Button onClick={handleRetry}>Try Again</Button>
                </div>
              </div>
            )}

            {/* Completed message */}
            {isCompleted && (
              <motion.p
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-sm text-muted-foreground"
              >
                Redirecting to your course…
              </motion.p>
            )}
          </div>

          {/* ─── Right Column: Preview Skeleton ─── */}
          <div className="w-full lg:w-[60%]">
            <CoursePreviewSkeleton activeStep={activeStep} />
          </div>
        </div>
      </div>
    </DashboardShell>
  );
};

export default CourseCreatingPage;
